import React, { useEffect, useState } from 'react';
import { GetStreams, GetStream } from '../utilities/calls';

/**
 * StreamSelectModal
 *
 * Modal dialog that lets the user pick a single Stream (by GUID) from a
 * dropdown populated via GetStreams(), and shows a lightweight preview of
 * the selected Stream's fields (ID, IndividualID, shared, confirmed).
 *
 * Props:
 *  - isOpen: boolean          - controls visibility
 *  - onClose: () => void      - called when the modal should close (backdrop, Esc, Cancel, on Confirm)
 *  - onConfirm: (streamid) => void - called with the selected Stream ID of object on Confirm
 */
export default function StreamSelectModal({ isOpen, onClose, onConfirm }) {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [selectedStream, setSelectedStream] = useState(null);

  // Fetch streams whenever the modal opens; reset local state on close.
  useEffect(() => {
    if (!isOpen) {
      setSelectedId('');
      setInputValue('');
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    GetStreams()
      .then((data) => {
        if (!cancelled) setStreams(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load streams');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    let cancelled = false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    
    if (selectedId) {
        const isValid = uuidRegex.test(selectedId); 
        if (isValid) {
            GetStream(selectedId)
              .then((data) => {
                if (!cancelled) setSelectedStream(data);
              })
              .catch((err) => {
                if (!cancelled) setError(err?.message || 'Failed to load stream');
              })
              .finally(() => {
                if (!cancelled) setLoading(false);
              });
        } else {
            setSelectedStream(null);
        }
    } else {
        setSelectedStream(null);
    }
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const handleConfirm = () => {
    if (selectedStream) { 
        onConfirm?.("jrn:stream:" + selectedStream.ID);
        onClose?.();
    } else {
        onConfirm?.(selectedId);
        onClose?.();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        className="flex max-h-[85vh] w-[420px] max-w-[90vw] flex-col overflow-y-auto rounded-lg border border-neutral-800 bg-neutral-900 text-neutral-100 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="stream-modal-title"
      >
        <div className="flex items-center justify-between border-b border-neutral-800 px-5 py-4">
          <h2 id="stream-modal-title" className="text-base font-semibold">
            Select a Stream
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="text-xl leading-none text-neutral-400 transition-colors hover:text-neutral-100"
          >
            &times;
          </button>
        </div>

        <div className="px-5 py-5">
          {loading && <p className="text-sm text-neutral-400">Loading streams…</p>}
          {error && <p className="text-sm text-red-400">{error}</p>}

          {!loading && !error && (
            <div className="w-full">
              <label
                htmlFor="stream-select"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-neutral-400"
              >
                Stream or Image URL
              </label>
              <input
                id="stream-select"
                type="text"
                value={inputValue}
                onChange={(e) => {
                    setInputValue(e.target.value);
                    setSelectedId(e.target.value);
                }}
                onFocus={() => setInputValue("")}
                onBlur={() => setInputValue(selectedId)}
                list="stream-options"
                className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Select from the dropdown or type Image URL http/https..."
              />
                <datalist id="stream-options">
                    {streams.map((stream) => (
                      <option key={stream.ID} value={stream.ID}>
                        {stream.ID}
                      </option>
                    ))}
                </datalist>
            </div>
          )}

          {selectedStream && (
            <div className="mt-5 rounded-md border border-neutral-800 bg-neutral-950 p-4">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Preview
              </h3>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
                <dt className="text-xs text-neutral-500">ID</dt>
                <dd className="break-all text-sm text-neutral-200">jrn:stream:{selectedStream.ID}</dd>

                <dt className="text-xs text-neutral-500">Individual ID</dt>
                <dd className="break-all text-sm text-neutral-200">
                  {selectedStream.IndividualID}
                </dd>
                
                <dt className="text-xs text-neutral-500">Name</dt>
                <dd className="break-all text-sm text-neutral-200">
                  {selectedStream.tags?.Name?.Value}
                </dd>
                
                <dt className="text-xs text-neutral-500">File Name</dt>
                <dd className="break-all text-sm text-neutral-200">
                  {selectedStream.tags?.__SYS_JW_FileName?.Value}
                </dd>
                
                <dt className="text-xs text-neutral-500">File Type</dt>
                <dd className="break-all text-sm text-neutral-200">
                  {selectedStream.tags?.__SYS_JW_Type?.Value}
                </dd>
                
                <dt className="text-xs text-neutral-500">Shared</dt>
                <dd className="text-sm text-neutral-200">
                  {selectedStream.shared ? 'Yes' : 'No'}
                </dd>

                <dt className="text-xs text-neutral-500">Confirmed</dt>
                <dd className="text-sm text-neutral-200">
                  {selectedStream.confirmed ? 'Yes' : 'No'}
                </dd>
              </dl>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-neutral-800 px-5 py-3.5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-700 bg-neutral-800 px-4 py-2 text-sm text-neutral-200 transition-colors hover:bg-neutral-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedId}
            onClick={handleConfirm}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-blue-900 disabled:text-blue-300"
          >
            Select
          </button>
        </div>
      </div>
    </div>
  );
}
